import * as THREE from 'three';
import { InterpolatedProperty } from '../properties/InterpolatedProperty';
import { SettingsProperty } from '../properties/SettingsProperty';
import { Vector3, Quaternion } from '../types';
import { SimpleSettingsProperty } from '../properties/SimpleSettingsProperty';
import { Logger } from './Logger';


export enum TransformPropertyMode {
    SIMPLE = 'simple',
    INTERPOLATED = 'interpolated'
  }

export class Transform {
    // Transform properties are now of a base type
    public readonly position: SettingsProperty<Vector3>;
    public readonly rotation: SettingsProperty<Quaternion>;
    public readonly scale: SettingsProperty<Vector3>;

    public tag : string | null = null;

    // Parent-child relationships
    private _parent: Transform | null = null;
    private _children: Transform[] = [];

    // World transform is now also a set of observable properties.
    // This is useful for debugging and for UI elements that need to track the final world coordinates.
    public readonly worldPosition: SimpleSettingsProperty<Vector3>;
    public readonly worldQuaternion: SimpleSettingsProperty<Quaternion>;
    public readonly worldScale: SimpleSettingsProperty<Vector3>;

    /**
     * Canonical world transform shared with Three.js semantics.
     *
     * Keeping the full matrix is important: component-wise scale/quaternion
     * composition loses shear when a non-uniformly scaled parent has a rotated
     * child. Three.js keeps that information in matrixWorld, so the engine must
     * do the same if child labels and detached CSS3D objects are to agree with
     * the rendered scene graph.
     */
    private readonly _worldMatrix: THREE.Matrix4 = new THREE.Matrix4();

    // Private flags to track local changes
    private _isLocalPositionDirty: boolean = true;
    private _isLocalRotationDirty: boolean = true;
    private _isLocalScaleDirty: boolean = true;

    private static DEFAULT_INTERPOLATION_MODE: TransformPropertyMode = TransformPropertyMode.INTERPOLATED;
    private static DEFAULT_INTERPOLATION_DURATION = 3;
    public mode: TransformPropertyMode;

    // Public getters that check the entire hierarchy
    public get isPositionDirty(): boolean {
        return this._isLocalPositionDirty || (this._parent?.isPositionDirty ?? false);
    }

    public get isRotationDirty(): boolean {
        return this._isLocalRotationDirty || (this._parent?.isRotationDirty ?? false);
    }

    public get isScaleDirty(): boolean {
        return this._isLocalScaleDirty || (this._parent?.isScaleDirty ?? false);
    }

    constructor(
        position: Vector3 | null,
        rotation: Quaternion | null,
        scale: Vector3 | null,
        mode: TransformPropertyMode = Transform.DEFAULT_INTERPOLATION_MODE,
        useDefaults: boolean = true
    ) {
        this.tag = null;
        this.mode = mode;

        const group = 'Transform';
        
        const defaultPos = useDefaults ? Vector3.zero() : null;
        const defaultRot = useDefaults ? Quaternion.identity() : null;
        const defaultScale = useDefaults ? Vector3.one() : null;
        
        if (this.mode === TransformPropertyMode.INTERPOLATED) {
            this.position = new InterpolatedProperty("Position", position, defaultPos, Transform.DEFAULT_INTERPOLATION_DURATION, group).withCurrentValueInUI();
            this.rotation = new InterpolatedProperty("Rotation", rotation, defaultRot, Transform.DEFAULT_INTERPOLATION_DURATION, group).withCurrentValueInUI();
            this.scale = new InterpolatedProperty("Scale", scale, defaultScale, Transform.DEFAULT_INTERPOLATION_DURATION, group);
        } 
        else 
        {
            this.position = new SimpleSettingsProperty("Position", position, defaultPos, group);
            this.rotation = new SimpleSettingsProperty("Rotation", rotation, defaultRot, group);
            this.scale = new SimpleSettingsProperty("Scale", scale, defaultScale, group);
        }
        
        // Set up change listeners to mark matrices as dirty
        this.position.onChange = () => this.markPositionDirty();
        this.rotation.onChange = () => this.markRotationDirty();
        this.scale.onChange = () => this.markScaleDirty();

        // Initialize world transform properties
        const worldGroup = 'World Transform (Live)';
        this.worldPosition = new SimpleSettingsProperty("World Position", Vector3.zero(), null, worldGroup).setReadOnly(true);
        this.worldQuaternion = new SimpleSettingsProperty("World Rotation", Quaternion.identity(), null, worldGroup).setReadOnly(true);
        this.worldScale = new SimpleSettingsProperty("World Scale", Vector3.one(), null, worldGroup).setReadOnly(true);

        // // Add logging for when the final world transform is updated
        // this.worldPosition.on((v) => //Logger.Instance.Debug(`[Transform] WorldPos Updated: ${v?.toString()}`));
        // this.worldQuaternion.on((q) => //Logger.Instance.Debug(`[Transform] WorldRot Updated: ${q?.toString()}`));
        // this.worldScale.on((s) => //Logger.Instance.Debug(`[Transform] WorldScale Updated: ${s?.toString()}`));
    }

    // Public getter for the combined dirty state
    public get isDirty(): boolean {
        return this.isPositionDirty || this.isRotationDirty || this.isScaleDirty;
    }

    /**
     * Computes and returns the local transformation matrix.
     * @returns THREE.Matrix4 representing the local transform.
     */
    public getMatrix(): THREE.Matrix4 {
        const matrix = new THREE.Matrix4();
        const pos = this.position.value ?? Vector3.zero();
        const rot = this.rotation.value ?? Quaternion.identity();
        const scl = this.scale.value ?? Vector3.one();
        matrix.compose(
            new THREE.Vector3(pos.x, pos.y, pos.z),
            new THREE.Quaternion(rot.x, rot.y, rot.z, rot.w),
            new THREE.Vector3(scl.x, scl.y, scl.z)
        );
        return matrix;
    }

    /**
     * Computes and returns the world transformation matrix.
     * @returns THREE.Matrix4 representing the world transform.
     */
    public getWorldMatrix(): THREE.Matrix4 {
        this.updateWorldTransform();
        return this._worldMatrix.clone();
    }

    // Parent-child relationship management
    setParent(parent: Transform | null): void {
        if (this._parent === parent) return;

        // Remove from old parent
        if (this._parent) {
            const index = this._parent._children.indexOf(this);
            if (index !== -1) {
                this._parent._children.splice(index, 1);
            }
        }

        // Set new parent
        this._parent = parent;
        if (parent) {
            parent._children.push(this);
        }

        this.markAllDirty();
    }

    getParent(): Transform | null {
        return this._parent;
    }

    getChildren(): Transform[] {
        return [...this._children]; // Return copy to prevent external modification
    }

    addChild(child: Transform): void {
        child.setParent(this);
    }

    removeChild(child: Transform): void {
        child.setParent(null);
    }

    // World space calculations
    getWorldPosition(): Vector3 {
        this.updateWorldTransform();
        return (this.worldPosition.value ?? Vector3.zero()).clone();
    }

    getWorldQuaternion(): Quaternion {
        this.updateWorldTransform();
        return (this.worldQuaternion.value ?? Quaternion.identity()).clone();
    }

    getWorldScale(): Vector3 {
        this.updateWorldTransform();
        return (this.worldScale.value ?? Vector3.one()).clone();
    }

    // Set world position (converts to local space)
    setWorldPosition(worldPos: Vector3): void {
        if (!this._parent) {
            this.position.value = worldPos.clone();
            return;
        }

        const inverseParent = this._parent.getWorldMatrix().invert();
        const localPos = new THREE.Vector3(worldPos.x, worldPos.y, worldPos.z)
            .applyMatrix4(inverseParent);
        this.position.value = new Vector3(localPos.x, localPos.y, localPos.z);
    }

    // Set world rotation (converts to local space)
    setWorldRotation(worldRot: Quaternion): void {
        if (!this._parent) {
            this.rotation.value = worldRot.clone();
            return;
        }

        const parentWorldRot = this._parent.getWorldQuaternion();
        const invParentRot = parentWorldRot.inverse();
        const localRot = invParentRot.multiply(worldRot);

        this.rotation.value = localRot;
    }

    // Transform point from local to world space
    transformPoint(localPoint: Vector3): Vector3 {
        const point = new THREE.Vector3(localPoint.x, localPoint.y, localPoint.z)
            .applyMatrix4(this.getWorldMatrix());
        return new Vector3(point.x, point.y, point.z);
    }

    // Transform direction from local to world space (no translation)
    transformDirection(localDirection: Vector3): Vector3 {
        const direction = new THREE.Vector3(localDirection.x, localDirection.y, localDirection.z)
            .transformDirection(this.getWorldMatrix());
        return new Vector3(direction.x, direction.y, direction.z);
    }

    // Inverse transform point from world to local space
    inverseTransformPoint(worldPoint: Vector3): Vector3 {
        const point = new THREE.Vector3(worldPoint.x, worldPoint.y, worldPoint.z)
            .applyMatrix4(this.getWorldMatrix().invert());
        return new Vector3(point.x, point.y, point.z);
    }

    // Direction vector methods
    getForwardVector(): Vector3 {
        const worldRot = this.getWorldQuaternion();
        return worldRot.rotateVector(Vector3.forward());
    }

    getUpVector(): Vector3 {
        const worldRot = this.getWorldQuaternion();
        return worldRot.rotateVector(Vector3.up());
    }

    getRightVector(): Vector3 {
        const worldRot = this.getWorldQuaternion();
        return worldRot.rotateVector(Vector3.right());
    }

    // --- Orientation Control ---

    /**
     * The direction the transform is pointing, in world space.
     */
    public get direction(): Vector3 {
        // This is equivalent to getForwardVector but named for intuitive API use.
        return this.getForwardVector();
    }

    /**
     * The 'up' vector of the transform, in world space.
     */
    public get up(): Vector3 {
        return this.getUpVector();
    }
    
    /**
     * The 'right' vector of the transform, in world space.
     */
    public get right(): Vector3 {
        return this.getRightVector();
    }

    /**
     * Sets the orientation of the transform based on a direction and an up vector.
     * @param direction The direction to look in.
     * @param up The desired 'up' vector, which will be made orthogonal to the direction.
     */
    public setOrientation(direction: Vector3, up: Vector3 = Vector3.up()): void {
        const newRotation = Quaternion.lookDir(direction, up);
        this.rotation.value = newRotation;
    }

    // --- End Orientation Control ---

    // Reset to identity transform
    reset(): void {
        this.position.setImmediate(Vector3.zero());
        this.rotation.setImmediate(Quaternion.identity());
        this.scale.setImmediate(Vector3.one());
    }

    // Private methods to mark the LOCAL transform as dirty
    private markAllDirty(): void {
        this._isLocalPositionDirty = true;
        this._isLocalRotationDirty = true;
        this._isLocalScaleDirty = true;
        // Mark all children as dirty too
        for (const child of this._children) {
            child.markAllDirty();
        }
    }

    private markPositionDirty(): void {
        this._isLocalPositionDirty = true;
        this.markDescendantsDirty();
    }

    /** Force all dirty flags — used after Three.js re-parenting */
    public markDirty(): void {
        this._isLocalPositionDirty = true;
        this._isLocalRotationDirty = true;
        this._isLocalScaleDirty = true;
        this.markDescendantsDirty();
    }

    private markRotationDirty(): void {
        this._isLocalRotationDirty = true;
        this.markDescendantsDirty();
    }

    private markScaleDirty(): void {
        this._isLocalScaleDirty = true;
        this.markDescendantsDirty();
    }

    /**
     * A child world matrix depends on every ancestor component. Propagate a
     * durable dirty flag instead of deriving child dirtiness only from the
     * parent's current flag; otherwise an early parent world read can consume
     * the flag before the child has recomputed.
     */
    private markDescendantsDirty(): void {
        for (const child of this._children) {
            child.markAllDirty();
        }
    }

    private updateWorldTransform(): void {
        const isInterpolating = this.hasInterpolatingProperties();
        
        // This check is now simpler because isDirty recursively checks the parent.
        if (!this.isDirty && !isInterpolating) {
            return; // No update needed
        }

        const localMatrix = this.getMatrix();
        if (this._parent) {
            this._parent.updateWorldTransform();
            this._worldMatrix.multiplyMatrices(this._parent._worldMatrix, localMatrix);
        } else {
            this._worldMatrix.copy(localMatrix);
        }

        const worldPosition = new THREE.Vector3();
        const worldQuaternion = new THREE.Quaternion();
        const worldScale = new THREE.Vector3();
        this._worldMatrix.decompose(worldPosition, worldQuaternion, worldScale);

        this.worldPosition.value = new Vector3(worldPosition.x, worldPosition.y, worldPosition.z);
        this.worldQuaternion.value = new Quaternion(
            worldQuaternion.x,
            worldQuaternion.y,
            worldQuaternion.z,
            worldQuaternion.w
        );
        this.worldScale.value = new Vector3(worldScale.x, worldScale.y, worldScale.z);

        this._isLocalScaleDirty = false;
        this._isLocalRotationDirty = false;
        this._isLocalPositionDirty = false;
    }

    public hasInterpolatingProperties(): boolean {
        // We can check if the property has an 'isInterpolating' getter
        if ('isInterpolating' in this.position) {
            return (this.position as InterpolatedProperty<Vector3>).isInterpolating ||
                   (this.rotation as InterpolatedProperty<Quaternion>).isInterpolating ||
                   (this.scale as InterpolatedProperty<Vector3>).isInterpolating ||
                   (this._parent?.hasInterpolatingProperties() ?? false);
        }
        return false;
    }

    // Static factory methods
    static identity(mode: TransformPropertyMode = Transform.DEFAULT_INTERPOLATION_MODE): Transform {
        return new Transform(Vector3.zero(), Quaternion.identity(), Vector3.one(), mode);
    }

    static fromPosition(position: Vector3, mode: TransformPropertyMode = Transform.DEFAULT_INTERPOLATION_MODE): Transform {
        return new Transform(position, Quaternion.identity(), Vector3.one(), mode);
    }

    static fromRotation(rotation: Quaternion, mode: TransformPropertyMode = Transform.DEFAULT_INTERPOLATION_MODE): Transform {
        return new Transform(Vector3.zero(), rotation, Vector3.one(), mode);
    }

    static fromScale(scale: Vector3, mode: TransformPropertyMode = Transform.DEFAULT_INTERPOLATION_MODE): Transform {
        return new Transform(Vector3.zero(), Quaternion.identity(), scale, mode);
    }

    /**
     * Creates a Transform with proper default values for independent SceneNodes.
     * Fixed: useDefaults=true ensures standard values (0,0,0), (0,0,0,1), (1,1,1)
     */
    static createInheritable(mode: TransformPropertyMode = Transform.DEFAULT_INTERPOLATION_MODE): Transform {
        return new Transform(null, null, null, mode, true); // ← FIX: useDefaults=true
    }

}
